import { observer } from 'mobx-react-lite'
import { ReactElement } from 'react'
import { Translate } from 'src/i18n'
import { useListsStore } from 'src/store/lists/ListsStore'
import { useUiStateStore } from 'src/store/uiState/UiStateStore'
import { Button } from 'src/ui-components'
import styled from 'styled-components'
import { defaultTheme } from 'src/theme'
import BaseDrawer from 'src/ui-components/modals/BaseDrawer'
import ListNode from '../../../../../store/lists/ListNode'
import CollapsibleGroup from './CollapsibleGroup'
import { FlatGroup } from '../../../../../store/lists/interfaces'

export const MoveGroupPartsDrawer = observer((): ReactElement => {
  const {
    showMoveToAnotherListDrawer,
    setMoveToAnotherListDrawer,
    setMoveToNextListDrawer,
  } = useUiStateStore()

  const listStore = useListsStore()

  const moveItems = listStore.getSelectedPartsBasket()?.items
  const selectedPartsCount = listStore.getAllSelectedParts()?.length

  const drawerTitle = `${Translate('moveToAnotherList')} (${
    selectedPartsCount
  } ${Translate('items')})`

  const handleCancel = () => {
    setMoveToAnotherListDrawer(false)
    listStore.clearAllSelections()
  }

  const getParentCategory = (listId: number): ListNode => {
    const category = listStore.root
      .getChildren()
      .find((c) => c.getChildren().find((i) => i.id === listId))

    return category
  }

  const getFlatGroups = (): FlatGroup[] => {
    if (!moveItems?.length) return []

    return moveItems.flatMap((item) => {
      const category = getParentCategory(item.listId)
      const list = category?.getChildren().find((l) => l.id === item.listId)
      const flattenGroups = item.groups?.map((group) => {
        const groupItem = list?.getChildren().find((g) => g.id === group.id)

        return {
          category: category?.value ?? '',
          list: list?.value ?? '',
          group: groupItem?.value ?? '',
          parts: group.parts,
        }
      })

      return flattenGroups
    })
  }

  const rows = getFlatGroups()

  const drawerBody = (
    <Container>
      {rows?.map((r, idx) => (
        <CollapsibleGroup key={`cat_list_group_row_${idx}`} {...r} />
      ))}
    </Container>
  )

  const drawerFooter = (
    <BottomContainer>
      <ButtonsContainer>
        <Button text="cancel" onClick={handleCancel} variant="secondary" />

        {selectedPartsCount > 0 && (
          <Button
            text={`${Translate('nextChooseList')}`}
            onClick={() => {
              setMoveToAnotherListDrawer(false)
              setMoveToNextListDrawer(true)
            }}
            variant="primary"
          />
        )}
      </ButtonsContainer>
    </BottomContainer>
  )

  return (
    <BaseDrawer
      open={showMoveToAnotherListDrawer}
      title={drawerTitle}
      body={drawerBody}
      onClose={() => setMoveToAnotherListDrawer(false)}
      footer={drawerFooter}
      width="800px"
    />
  )
})

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: end;
  margin: 10px 0px;
  align-items: center;
`

const BottomContainer = styled.div`
  height: 50px;
  width: 100%;
  background-color: ${defaultTheme.colors.white};
  justify-content: end;
  align-items: center;
  display: flex;
`
